import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router';
import { useAuthentication, useGetAuthenticatedUser } from '@connect-x-app/hybrid-state';
import {
  ADMIN_CONFIG_PERMISSIONS,
  AdminConfigPermissions,
  AuthenticatedUser,
  RolesPermissions,
} from '@connect-x-app/shared-domain-models';
import { hasAnyPermission, hasPermission } from '@connect-x-app/shared-utils';
import {
  AdminQuickReplies,
  ChannelConfigurations,
  Connect,
  CreateSmartAction,
  General,
  Role,
  Roles,
  SmartActions,
  SystemMessages,
  UpdateSmartAction,
} from '@connect-x-app/web-feature-admin';
import { LicenseAgreement } from '@connect-x-app/web-feature-license';
import { Spinner } from '@connect-x-app/web-ui';

import { AuthorizedLayout } from './routing/authorized-layout/AuthorizedLayout';
import { NoPermissions } from './routing/no-permissions/NoPermissions';

import './global-style.scss';

export const AppAdmin = () => {
  document.title = 'Connect Admin';

  const { t } = useTranslation();
  const { setUser, user: authenticatedUser } = useAuthentication();
  const {
    data: user,
    isPending: isFetchingAuthentication,
    isSuccess: isAuthenticatedUserFetched,
  } = useGetAuthenticatedUser(true);

  useEffect(() => {
    if (user && isAuthenticatedUserFetched) {
      setUser(user);
    }
  }, [setUser, user, isAuthenticatedUserFetched]);

  if (isFetchingAuthentication && !authenticatedUser) {
    return <Spinner overlay>{t('SHARED.LOADING_APPLICATION')}</Spinner>;
  }

  return getRoutes(authenticatedUser);
};

const getRoutes = (user: AuthenticatedUser | null) => {
  if (user) {
    if (user.hasAcceptedLicenseAgreement) {
      return <AuthenticatedRoutes user={user} />;
    }
    return <LicenseAgreementRoutes />;
  }
  return <Routes />;
};

type AuthenticatedRoutesProps = {
  user: AuthenticatedUser;
};

const AuthenticatedRoutes: FC<AuthenticatedRoutesProps> = ({ user }) => {
  document.title = 'Connect Admin';
  const { t } = useTranslation();

  const generalAllowed = useMemo(() => hasAnyPermission(user.permissions, ADMIN_CONFIG_PERMISSIONS), [user.permissions]);

  const rolesAllowed = useMemo(() => hasPermission(user.permissions, RolesPermissions.VIEW), [user.permissions]);
  const adminConfigAllowed = useMemo(() => hasPermission(user.permissions, AdminConfigPermissions.VIEW), [user.permissions]);
  const createSmartActionAllowed = useMemo(
    () => hasPermission(user.permissions, AdminConfigPermissions.WRITE),
    [user.permissions],
  );
  const adminAllowed = rolesAllowed || adminConfigAllowed;

  if (!adminAllowed) {
    return <NoPermissions description={t('ADMIN.PERMISSION_DENIED.DESCRIPTION')} title={t('ADMIN.PERMISSION_DENIED.TITLE')} />;
  }

  return (
    <Routes>
      <Route element={<AuthorizedLayout />}>
        <Route element={<Connect />} path="connect">
          <Route element={<Navigate replace to={generalAllowed ? 'general' : 'roles'} />} index />
          {generalAllowed ? (
            <Route element={<General />} path="general">
              {adminConfigAllowed ? (
                <>
                  <Route element={<Navigate replace to="smart-actions" />} index />
                  <Route element={<SmartActions />} path="smart-actions" />
                  <Route element={<UpdateSmartAction />} path="smart-actions/edit/:id" />
                  {createSmartActionAllowed ? <Route element={<CreateSmartAction />} path="smart-actions/new" /> : null}
                  <Route element={<AdminQuickReplies />} path="quick-replies" />
                  <Route element={<SystemMessages />} path="system-messages" />
                  <Route element={<ChannelConfigurations />} path="channels" />
                </>
              ) : null}
            </Route>
          ) : null}
          {rolesAllowed ? (
            <>
              <Route element={<Roles />} path="roles" />
              <Route element={<Role />} path="roles/:roleId/*" />
            </>
          ) : null}
        </Route>
      </Route>
      <Route element={<Navigate to="/admin/connect" />} path="*" />
    </Routes>
  );
};

const LicenseAgreementRoutes: FC = () => {
  return (
    <Routes>
      <Route element={<LicenseAgreement />} path="*" />
    </Routes>
  );
};
